import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

const PLATFORM_ADMIN_EMAILS = new Set([
  "mjnayr@yahoo.com",
  "rmelhus@nlcschool.org",
]);

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSignIn = async (event) => {
    event.preventDefault();
    setError("");

    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }

    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(
        auth,
        email.trim().toLowerCase(),
        password
      );
      const user = result.user;
      const normalizedEmail = (user.email || "").toLowerCase();
      const profileSnap = await getDoc(doc(db, "users", user.uid));

      if (!profileSnap.exists()) {
        if (PLATFORM_ADMIN_EMAILS.has(normalizedEmail)) {
          navigate("/platform-admin", { replace: true });
          return;
        }

        await signOut(auth);
        setError(
          "Your sign-in exists, but it is not connected to a school. Contact your school administrator."
        );
        return;
      }

      const profile = profileSnap.data();
      const schoolId = profile.schoolId;
      if (!schoolId) {
        await signOut(auth);
        setError("Your account is not assigned to a school. Contact your administrator.");
        return;
      }

      const requestedPath = searchParams.get("redirect") || "";
      const safeRedirect = requestedPath.startsWith(`/${schoolId}/`)
        ? requestedPath
        : "";
      if (safeRedirect) {
        navigate(safeRedirect, { replace: true });
      } else if (profile.role === "student") {
        navigate(`/${schoolId}/student`, { replace: true });
      } else {
        navigate(`/${schoolId}/admin`, { replace: true });
      }
    } catch (signInError) {
      console.error("Sign in error:", signInError);
      setError("Unable to sign in. Check your email and password and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 480,
        margin: "3rem auto",
        padding: "2rem",
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
      }}
    >
      <h2 style={{ marginBottom: "0.25rem" }}>🎟 PBIS Portal Login</h2>
      <p style={{ color: "#555", marginBottom: "1rem" }}>
        Sign in with the account provided by your school.
      </p>

      <form onSubmit={handleSignIn}>
        <label htmlFor="login-email" style={{ fontWeight: 600 }}>Email</label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@school.org"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          style={{
            width: "100%",
            padding: "0.75rem",
            border: "1px solid #ccc",
            borderRadius: 6,
            marginBottom: "0.75rem",
          }}
        />

        <label htmlFor="login-password" style={{ fontWeight: 600 }}>Password</label>
        <input
          id="login-password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          style={{
            width: "100%",
            padding: "0.75rem",
            border: "1px solid #ccc",
            borderRadius: 6,
            marginBottom: "1rem",
          }}
        />

        {error && (
          <div
            role="alert"
            style={{
              background: "#fef2f2",
              color: "#991b1b",
              padding: "0.75rem",
              borderRadius: 6,
              marginBottom: "1rem",
              border: "1px solid #fecaca",
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: "100%",
            background: "#3b82f6",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "0.75rem 1rem",
            fontWeight: 600,
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

export default Login;
